import type { TeamColorStyle } from "../pages/logger/components/molecules/TacticalPlayerNode";

export const TEAM_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-purple-100 text-purple-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
  "bg-indigo-100 text-indigo-700",
  "bg-emerald-100 text-emerald-700",
] as const;

/**
 * `textOnDark` is the Tailwind ~400 shade of each primary's hue — chosen
 * because it clears >=4.5:1 (WCAG AA) against the dark panel background
 * DARK_PANEL_BG (#1e293b / surface-dark-card), unlike the 700-level
 * `primary` swatches which are tuned for use as solid fills, not as text
 * on a dark surface.
 */
const PALETTE_DEFS = [
  {
    primary: "#1d4ed8",
    contrast: "#ffffff",
    softBg: "rgba(29, 78, 216, 0.18)",
    softBorder: "rgba(147, 197, 253, 0.75)",
    labelBg: "rgba(30, 64, 175, 0.92)",
    textOnDark: "#60a5fa",
  },
  {
    primary: "#15803d",
    contrast: "#ffffff",
    softBg: "rgba(21, 128, 61, 0.18)",
    softBorder: "rgba(134, 239, 172, 0.75)",
    labelBg: "rgba(22, 101, 52, 0.92)",
    textOnDark: "#4ade80",
  },
  {
    primary: "#7e22ce",
    contrast: "#ffffff",
    softBg: "rgba(126, 34, 206, 0.2)",
    softBorder: "rgba(216, 180, 254, 0.75)",
    labelBg: "rgba(107, 33, 168, 0.92)",
    textOnDark: "#c084fc",
  },
  {
    primary: "#b45309",
    contrast: "#ffffff",
    softBg: "rgba(180, 83, 9, 0.2)",
    softBorder: "rgba(253, 230, 138, 0.75)",
    labelBg: "rgba(146, 64, 14, 0.92)",
    textOnDark: "#fbbf24",
  },
  {
    primary: "#be123c",
    contrast: "#ffffff",
    softBg: "rgba(190, 18, 60, 0.2)",
    softBorder: "rgba(253, 164, 175, 0.75)",
    labelBg: "rgba(136, 19, 55, 0.92)",
    textOnDark: "#fb7185",
  },
  {
    primary: "#0e7490",
    contrast: "#ffffff",
    softBg: "rgba(14, 116, 144, 0.18)",
    softBorder: "rgba(165, 243, 252, 0.75)",
    labelBg: "rgba(14, 116, 144, 0.92)",
    textOnDark: "#22d3ee",
  },
  {
    primary: "#4338ca",
    contrast: "#ffffff",
    softBg: "rgba(67, 56, 202, 0.2)",
    softBorder: "rgba(199, 210, 254, 0.75)",
    labelBg: "rgba(55, 48, 163, 0.92)",
    textOnDark: "#818cf8",
  },
  {
    primary: "#047857",
    contrast: "#ffffff",
    softBg: "rgba(4, 120, 87, 0.2)",
    softBorder: "rgba(110, 231, 183, 0.75)",
    labelBg: "rgba(6, 95, 70, 0.92)",
    textOnDark: "#34d399",
  },
] as const;

interface TeamIdentity {
  id?: string | number | null;
  team_id?: string | number | null;
  short_name?: string | null;
  name?: string | null;
}

/** Optional team color overrides from backend `primary_color`/`secondary_color`. */
interface TeamColorOverride {
  primary_color?: string | null;
  secondary_color?: string | null;
}

export type TeamWithColors = TeamIdentity & TeamColorOverride;

export const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const resolveTeamPaletteKey = (team: TeamIdentity): string => {
  const candidates = [
    team.short_name,
    team.name,
    team.id != null ? String(team.id) : "",
    team.team_id != null ? String(team.team_id) : "",
  ];

  const key = candidates
    .map((item) => String(item ?? "").trim())
    .find((item) => item.length > 0);

  return key ?? "PROMATCH_DEFAULT_TEAM";
};

export const resolveTeamPaletteIndex = (team: TeamIdentity): number => {
  const key = resolveTeamPaletteKey(team);
  return hashString(key) % TEAM_COLORS.length;
};

/**
 * Parse a 6-digit hex color string (#RRGGBB) into its RGB components.
 * Returns null for invalid input.
 */
export const parseHex = (hex: string): [number, number, number] | null => {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
};

/**
 * Build a TeamColorStyle from two user-supplied hex colors.
 *
 * - `bg`: primary_color (the main dot fill)
 * - `border`: secondary_color at 75% opacity — drives the player-node border ring
 *             and the team-label chip accent border. Falls back to primary at 45%
 *             opacity when secondary is absent or malformed.
 * - `labelBg`: primary_color darkened ~30% at 0.92 opacity
 * - `text`: always white (#ffffff) for legibility on the primary-colored dot
 *
 * The secondary_color is surfaced as `border` so all downstream consumers
 * (TacticalPlayerNode rings, bench-player dot outlines, team-chip borders) pick
 * it up automatically. Team-NAME text in TeamSelector uses secondary_color
 * directly from the TeamColorInfo prop (not from this style object).
 */
/** Minimum contrast (WCAG AA, normal text) for the jersey numeral on the kit fill. */
export const NUMBER_MIN_CONTRAST = 4.5;

const buildColorStyleFromHex = (
  primaryHex: string,
  secondaryHex: string,
): TeamColorStyle => {
  const rgb = parseHex(primaryHex);
  if (!rgb) {
    // Malformed primary hex — return a safe neutral style
    return {
      bg: primaryHex,
      border: `${primaryHex}73`,
      labelBg: `${primaryHex}eb`,
      text: "#ffffff",
      numberText: "#ffffff",
    };
  }
  const [r, g, b] = rgb;
  // Darken by ~30% for label background
  const dr = Math.round(r * 0.7);
  const dg = Math.round(g * 0.7);
  const db = Math.round(b * 0.7);

  // Use secondary color for the border ring. Fall back to translucent primary
  // if secondary is malformed (primary and secondary are both truthy at the
  // call-site in resolveTeamPalette, but the hex string itself may be invalid).
  const sRgb = parseHex(secondaryHex);
  const borderColor = sRgb
    ? `rgba(${sRgb[0]}, ${sRgb[1]}, ${sRgb[2]}, 0.75)`
    : `rgba(${r}, ${g}, ${b}, 0.45)`;
  // Jersey-number text: secondary color at full opacity for legibility on the
  // primary-colored dot. Falls back to white when secondary is malformed/absent.
  // EPIC-111 UIJ-R1-4: the secondary stays the ring (`border`); the numeral
  // only uses it when it clears 4.5:1 against the kit fill, else the better
  // of white / black does (e.g. red #dc2626 on a black kit is 4.35:1).
  const secondaryAsNumber = sRgb
    ? `rgba(${sRgb[0]}, ${sRgb[1]}, ${sRgb[2]}, 1)`
    : "#ffffff";
  const secondaryRatio = sRgb ? contrastRatio(secondaryHex, primaryHex) : null;
  const numberTextColor =
    sRgb && secondaryRatio !== null && secondaryRatio >= NUMBER_MIN_CONTRAST
      ? secondaryAsNumber
      : (contrastRatio("#ffffff", primaryHex) ?? 0) >=
          (contrastRatio("#000000", primaryHex) ?? 0)
        ? "#ffffff"
        : "#000000";

  return {
    bg: primaryHex,
    border: borderColor,
    labelBg: `rgba(${dr}, ${dg}, ${db}, 0.92)`,
    text: "#ffffff",
    numberText: numberTextColor,
  };
};

/** Relative luminance for WCAG contrast check (sRGB). */
export const relativeLuminance = (hex: string): number | null => {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const linearize = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = rgb.map(linearize);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two colors (1:1 to 21:1). Null if either hex is invalid. */
export const contrastRatio = (fgHex: string, bgHex: string): number | null => {
  const l1 = relativeLuminance(fgHex);
  const l2 = relativeLuminance(bgHex);
  if (l1 == null || l2 == null) return null;
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/** The dark panel surface team names/labels render on (surface-dark-card in tailwind.config.js). */
export const DARK_PANEL_BG = "#1e293b";

/**
 * Composite `hex` at `alpha` over an opaque `baseHex` (default `DARK_PANEL_BG`),
 * returning a fully-opaque hex approximation of how the color will actually
 * render as a translucent tint. Used by `pickReadableTextColor` so contrast is
 * checked against the REAL rendered surface, not the source hue alone —
 * `lightenForContrast`/`textOnDark` check a hex directly against
 * `DARK_PANEL_BG`, which is only correct when that hex IS what renders
 * opaquely; a low-alpha tint of even a near-white team color composited over
 * a dark base stays dark (safe for white text), but a HIGH-alpha tint (e.g.
 * the 0.75-alpha `border`/`softBg` a custom team's `secondary_color` drives)
 * can render close to its own source lightness — if that source is also
 * light, `textOnDark`'s dark-background assumption silently breaks (white
 * text on a near-white tint). Returns `hex` unchanged if either input is
 * malformed.
 */
export const blendHexOverBase = (
  hex: string,
  alpha: number,
  baseHex: string = DARK_PANEL_BG,
): string => {
  const fg = parseHex(hex);
  const bg = parseHex(baseHex);
  if (!fg || !bg) return hex;
  const mix = (f: number, b: number) => Math.round(f * alpha + b * (1 - alpha));
  const [r, g, b] = [mix(fg[0], bg[0]), mix(fg[1], bg[1]), mix(fg[2], bg[2])];
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

/**
 * Pick whichever of white or a dark near-black clears WCAG AA (>=4.5:1)
 * against `bgHex`, falling back to whichever is closer if neither does.
 * Unlike `lightenForContrast` (only ever lightens toward white, assuming the
 * background is dark), this handles a genuinely LIGHT background correctly —
 * e.g. a translucent tint built from a near-white team `primary_color` —
 * instead of always returning unreadable white-on-near-white.
 */
export const pickReadableTextColor = (
  bgHex: string,
  minRatio = 4.5,
): string => {
  const whiteRatio = contrastRatio("#ffffff", bgHex);
  const darkRatio = contrastRatio("#0f172a", bgHex);
  if (whiteRatio != null && whiteRatio >= minRatio) return "#ffffff";
  if (darkRatio != null && darkRatio >= minRatio) return "#0f172a";
  if (whiteRatio != null && darkRatio != null) {
    return whiteRatio >= darkRatio ? "#ffffff" : "#0f172a";
  }
  return "#ffffff";
};

const mixHexWithWhite = (hex: string, amount: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const mix = (v: number) => Math.round(v + (255 - v) * amount);
  return `#${rgb
    .map(mix)
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
};

/**
 * Lighten a hex color (by mixing toward white) until it clears `minRatio`
 * contrast against `bgHex`. Used so an arbitrary team `primary_color`
 * (including a dark navy a club might supply) still renders as legible
 * text on the dark panel background, instead of failing WCAG AA.
 */
export const lightenForContrast = (
  hex: string,
  bgHex: string = DARK_PANEL_BG,
  minRatio = 4.5,
): string => {
  if (!parseHex(hex)) return hex;
  const steps = 20;
  for (let step = 1; step <= steps; step += 1) {
    const candidate = mixHexWithWhite(hex, step / steps);
    const ratio = contrastRatio(candidate, bgHex);
    if (ratio != null && ratio >= minRatio) return candidate;
  }
  return "#ffffff";
};

/**
 * Normalize a team_id for use in PATCH /teams/{id} requests.
 *
 * The backend MatchTeam Pydantic model enforces ^[A-Z0-9_\-]+$ on the
 * embedded team_id, so the hex suffix written into match documents is
 * uppercased (e.g. "SAPRISSA_FF_COS_F_A11BC0").  The canonical teams
 * collection stores the id with a lowercase hex suffix produced by
 * uuid4().hex[:6] (e.g. "SAPRISSA_FF_COS_F_a11bc0").  Using the match's
 * embedded team_id directly for the PATCH therefore yields a 404.
 *
 * Fix: detect the 6-char hex suffix (last segment after the final "_") and
 * lowercase it so it matches the canonical key stored in db.teams.
 * Non-hex suffixes and ids with no underscore are returned unchanged.
 */
export const normalizeTeamId = (id: string): string => {
  const sep = id.lastIndexOf("_");
  if (sep === -1) return id;
  const suffix = id.slice(sep + 1);
  if (/^[0-9A-Fa-f]{6}$/.test(suffix)) {
    return id.slice(0, sep + 1) + suffix.toLowerCase();
  }
  return id;
};

export interface TeamPalette {
  fallbackClass: (typeof TEAM_COLORS)[number];
  primary: string;
  contrast: string;
  softBg: string;
  softBorder: string;
  labelBg: string;
  tacticalStyle: TeamColorStyle;
  /** True when the resolved palette came from user-supplied hex colors. */
  isCustom: boolean;
  /**
   * AA-contrast-safe (>=4.5:1) shade of `primary` for use as TEXT on the dark
   * panel background (DARK_PANEL_BG). `primary` itself is tuned for solid
   * fills/borders/swatches and often fails contrast as text — use this
   * instead wherever a team color renders as text on a dark surface.
   */
  textOnDark: string;
}

/**
 * Resolve a team's display palette.
 *
 * Priority:
 *   1. Both `primary_color` AND `secondary_color` set → build from hex.
 *   2. Either null → fall through to deterministic hash palette (unchanged).
 */
export const resolveTeamPalette = (team: TeamWithColors): TeamPalette => {
  const pc = team.primary_color;
  const sc = team.secondary_color;

  if (pc && sc) {
    const style = buildColorStyleFromHex(pc, sc);
    // Use a neutral Tailwind class as fallback for non-tactical contexts
    const index = resolveTeamPaletteIndex(team);
    return {
      fallbackClass: TEAM_COLORS[index],
      primary: pc,
      // Not a hardcoded white: a club whose primary is a pale yellow or
      // sky blue gets black text on its solid fills instead of an
      // unreadable white-on-white. `pickReadableTextColor` picks whichever
      // of white / near-black clears AA against `pc`.
      contrast: pickReadableTextColor(pc),
      softBg: style.border,
      softBorder: style.border,
      labelBg: style.labelBg,
      tacticalStyle: style,
      isCustom: true,
      textOnDark: lightenForContrast(pc),
    };
  }

  const index = resolveTeamPaletteIndex(team);
  const def = PALETTE_DEFS[index];

  return {
    fallbackClass: TEAM_COLORS[index],
    primary: def.primary,
    contrast: def.contrast,
    softBg: def.softBg,
    softBorder: def.softBorder,
    labelBg: def.labelBg,
    tacticalStyle: {
      bg: def.primary,
      border: def.softBorder,
      labelBg: def.labelBg,
      text: def.contrast,
      numberText: def.contrast,
    },
    isCustom: false,
    textOnDark: def.textOnDark,
  };
};
